import {BrowserModule} from '@angular/platform-browser';
import {NgModule} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {HttpClientModule, HTTP_INTERCEPTORS} from '@angular/common/http';

import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';

// Pages
import {
  LoginComponent, RegisterComponent, DashboardComponent, HomeComponent, ForgotComponent,
  RecoveryComponent, AccountInfoComponent, HeaderComponent, MainContentComponent, RegistrationComponent, ExpressSignupComponent,
} from './pages';

import {
  OverviewComponent, LocationComponent, OwnershipComponent, AssetsLiabilitiesComponent, DiversificationComponent, IncomeComponent,
  AdjustmentsComponent, BarriersEntryComponent,
  SpecialConsiderationComponent,
  ManagerSalaryComponent,
  PersonalGoodwillComponent,
  ProjectionTableComponent, RecurringRevenueComponent,
  RentDetailsComponent, ReplacementSalaryComponent, ReportComponent, RequiredInformationComponent,
  ReportFooterComponent, ReportHeaderComponent, ReportSideMenuComponent,
  ConclusionComponent, DivLoadingComponent, IstMarginComponent,
  IstActionListOptionComponent, IstExcelImportComponent, ExternalAnswerPopupComponent,
  WeightingTableComponent, IstExcelPasteComponent, AddInterimPopupComponent, ISTHelpPupupComponent, InvalidFormPopupComponent,
  PaymentPopupComponent, CreditPurchasePopupComponent, SuccessPageComponent, CheckoutComponent,
  WizardOwnerSalaryPopupComponent,
  LowValueWarningPopupComponent,
  ValueWarningPopupComponent,
  HowForecastingWorksPopupComponent,
  InterimRevenueWarningPopupComponent,
  IstActionContextMenuComponent,
  SpendCreditPopupComponent,
  UnlockReportPopupComponent,
  FutureProjectionsComponent,
  NotesComponent,
  IncomeInitWarningPopupComponent,
} from './pages/report';

// Directives
import {
  AlpaNumericWithSpaceDirective,
  ValidateSpaceReactiveFormDirective,
  CreditCardNumberDirective,
  AlphabetWithSpaceOnlyDirective,
  PercentageDirective, AlpaNumericWithSpaceReactiveDirective, AlphabetWithSpaceOnlyReactiveDirective, DisableRightClickDirective
} from './directives';

// Providers
import {
  AuthProvider, UserProvider, ReportProvider, CommonProvider, OrgProvider, AdministratorProvider
} from './providers';

// Services
import {UserService, LoaderService, ReportService} from './services';

import {
  ReportOptionComponent, ChangePasswordComponent,
  ConfirmPopupComponent, MyLoaderComponent, ReportPreviewLoaderComponent,
  ShowNoteModalComponent, ReportCopyVersionPopupComponent,
  FeedbackPopupComponent, AlertDialogComponent,
  AddUserInOrgModalComponent,
  UpdateUserInOrgModalComponent,
} from './components';

import {RouterModule} from '@angular/router';

import {ModalModule} from 'ngx-bootstrap/modal';
import {ToastrModule} from 'ngx-toastr';
import {BsDatepickerModule} from 'ngx-bootstrap/datepicker';
import {AccordionModule} from 'ngx-bootstrap/accordion';
import {ProgressbarModule} from 'ngx-bootstrap/progressbar';
import {TooltipModule} from 'ngx-bootstrap/tooltip';
import {PopoverModule} from 'ngx-bootstrap/popover';
import {ButtonsModule} from 'ngx-bootstrap/buttons';
import {BsDropdownModule} from 'ngx-bootstrap/dropdown';
import {PaginationModule} from 'ngx-bootstrap/pagination';
import {CollapseModule} from 'ngx-bootstrap/collapse';

import {LoaderInterceptor} from './interceptors/loader-interceptor';

import {NgChartsModule} from 'ng2-charts';
import {DatePipe} from '@angular/common';
import {ScrollToModule} from '@nicky-lenaers/ngx-scroll-to';
import {NotificationComponent} from './components/notification/notification.component';
import {ChatComponent} from './components/chat/chat.component';
import {NgxFileDropModule} from 'ngx-file-drop';
import {CurrencyPipe} from './pipes';
import {CustomFormsModule} from 'ng2-validation';
import {SharedModule} from './modules/shared-module/shared.module';

import {InjectableRxStompConfig, RxStompService, rxStompServiceFactory} from '@stomp/ng2-stompjs';
import {stompConfig} from './config/stomp.config';
import {SocketService} from './services/socket.service';

import {
  DashboardCustomSelectComponent,
  DashboardLoadingSkeletonComponent,
  DashboardReportComponent,
  OrderFormConfirmPopupComponent,
  ShareReportModalComponent,
  ReleaseThirdPartyModalComponent,
  PreparedForModalComponent,
  ConnectedToQuickbooksModalComponent,
  SelectCompaniesModalComponent
} from './pages/dashboard/components';

import {TransactionsHistoryComponent, PricingPlansComponent} from './pages/payment';

import {ToasterService} from './services/toaster.service';
import {SignupComponent} from './pages/signup/signup.component';
import {PageNotFoundComponent} from './pages/page-not-found/page-not-found.component';
import {RecaptchaModule, RecaptchaSettings, RECAPTCHA_SETTINGS} from 'ng-recaptcha';
import {QuillModule} from 'ngx-quill';
import {TimepickerModule} from 'ngx-bootstrap/timepicker';
import {environment} from 'src/environments/environment';
import {CommonService} from './services/common.service';
import {AngularSvgIconModule} from 'angular-svg-icon';
import {TransferReportModalComponent} from './pages/dashboard/components/transfer-report-modal/transfer-report-modal.component';
import {NgxStripeModule} from 'ngx-stripe';
import {ToolkitModule} from '@lendux/lendux-commons/toolkit';
import {ConfirmationPopupLargeComponent} from './components/confirmation-popup-large/confirmation-popup-large.component';
import {RemoveLeadingZeroDirective} from './modules/shared-module/directives/remove-leading-zero.directive';
import {ViewReportModalComponent} from './modals/view-report-modal/view-report-modal.component';

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    RegisterComponent,
    RegistrationComponent,
    DashboardComponent,
    HomeComponent,
    ForgotComponent,
    RecoveryComponent,
    // Directives
    ValidateSpaceReactiveFormDirective,
    AlpaNumericWithSpaceDirective,
    CreditCardNumberDirective,
    AlphabetWithSpaceOnlyDirective,
    PercentageDirective,
    AlpaNumericWithSpaceReactiveDirective,
    AlphabetWithSpaceOnlyReactiveDirective,
    DisableRightClickDirective,
    AccountInfoComponent,
    HeaderComponent,
    MainContentComponent,
    ReportOptionComponent,
    ShowNoteModalComponent,
    ReportCopyVersionPopupComponent,
    MyLoaderComponent,
    ReportPreviewLoaderComponent,
    ReportComponent,
    ReportSideMenuComponent,
    ReportFooterComponent,
    ReportHeaderComponent,
    OverviewComponent,
    SpecialConsiderationComponent,
    IncomeComponent,
    LocationComponent,
    OwnershipComponent,
    RentDetailsComponent,
    ManagerSalaryComponent,
    ReplacementSalaryComponent,
    ProjectionTableComponent,
    AssetsLiabilitiesComponent,
    AdjustmentsComponent,
    WeightingTableComponent,
    RequiredInformationComponent,
    ChangePasswordComponent,
    IstMarginComponent,
    PersonalGoodwillComponent,
    BarriersEntryComponent,
    RecurringRevenueComponent,
    ConfirmPopupComponent,
    DiversificationComponent,
    ConclusionComponent,
    DivLoadingComponent,
    NotificationComponent,
    DashboardCustomSelectComponent,
    IstActionListOptionComponent,
    IstActionContextMenuComponent,
    ChatComponent,
    IstExcelImportComponent,
    ExternalAnswerPopupComponent,
    IstExcelPasteComponent,
    AddInterimPopupComponent,
    ISTHelpPupupComponent,
    InvalidFormPopupComponent,
    PaymentPopupComponent,
    SpendCreditPopupComponent,
    CreditPurchasePopupComponent,
    UnlockReportPopupComponent,
    SuccessPageComponent,
    CheckoutComponent,
    WizardOwnerSalaryPopupComponent,
    DashboardLoadingSkeletonComponent,
    DashboardReportComponent,
    IncomeComponent,
    ShareReportModalComponent,
    OrderFormConfirmPopupComponent,
    LowValueWarningPopupComponent,
    HowForecastingWorksPopupComponent,
    InterimRevenueWarningPopupComponent,
    SignupComponent,
    FutureProjectionsComponent,
    NotesComponent,
    PageNotFoundComponent,
    ValueWarningPopupComponent,
    FeedbackPopupComponent,
    AlertDialogComponent,
    AddUserInOrgModalComponent,
    UpdateUserInOrgModalComponent,
    ReleaseThirdPartyModalComponent,
    PreparedForModalComponent,
    ConnectedToQuickbooksModalComponent,
    SelectCompaniesModalComponent,
    TransactionsHistoryComponent,
    PricingPlansComponent,
    ExpressSignupComponent,
    TransferReportModalComponent,
    IncomeInitWarningPopupComponent,
    ConfirmationPopupLargeComponent,
    ViewReportModalComponent
  ],
  imports: [
    BrowserModule,
    FormsModule,
    ReactiveFormsModule,
    SharedModule,
    BrowserAnimationsModule,
    HttpClientModule,
    AppRoutingModule,
    RouterModule,
    CollapseModule,
    BsDropdownModule.forRoot(),
    ModalModule.forRoot(),
    ToastrModule.forRoot({timeOut: 150000, toastClass: 'c-toastr--wrapper', positionClass: 'toast-top-right', closeButton: true}),
    AccordionModule.forRoot(),
    ProgressbarModule.forRoot(),
    BsDatepickerModule.forRoot(),
    TimepickerModule.forRoot(),
    TooltipModule.forRoot(),
    PopoverModule.forRoot(),
    ButtonsModule.forRoot(),
    ScrollToModule.forRoot(),
    PaginationModule.forRoot(),
    NgChartsModule,
    NgxFileDropModule,
    // CustomFormsModule,
    RecaptchaModule,
    QuillModule.forRoot(),
    AngularSvgIconModule.forRoot(),
    NgxStripeModule.forRoot(environment.STRIPE_KEY),
    ToolkitModule,
    RemoveLeadingZeroDirective
  ],
  exports: [NgxStripeModule],
  providers: [
    AuthProvider,
    UserProvider,
    ReportProvider,
    AdministratorProvider,
    OrgProvider,
    CommonProvider,
    // Services
    CommonService,
    UserService,
    ReportService,
    LoaderService,
    ToasterService,
    DatePipe,
    CurrencyPipe,
    SocketService,
    {
      provide: InjectableRxStompConfig,
      useValue: stompConfig
    },
    {
      provide: RxStompService,
      useFactory: rxStompServiceFactory,
      deps: [InjectableRxStompConfig]
    },
    {provide: HTTP_INTERCEPTORS, useClass: LoaderInterceptor, multi: true},
    {provide: RECAPTCHA_SETTINGS, useValue: {siteKey: '6LfilIAcAAAAAC7R38udATMjnOvBsMFllkUhVuus'} as RecaptchaSettings}
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
}
